import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { SmartLinkDecisionAction } from 'src/app/core/dtos/enums/smart-link-decision-action';
import { SmartLinkRejectReason } from 'src/app/core/dtos/enums/smart-link-reject-reason';
import { HttpSimplifiedErrorResponse } from 'src/app/core/interfaces/http-simplified-error-response';
import { ISmartLink } from 'src/app/core/models/i-smart-link';
import { ISmartLinkDecisionRequest } from 'src/app/core/models/i-smart-link-decision-request';
import {
  ISmartLinkRevokeRequest,
  ISmartLinkRevokeResponse,
} from 'src/app/core/models/i-smart-link-revoke-request';
import { ISmartLinkRunRequest } from 'src/app/core/models/i-smart-link-run-request';
import {
  ISmartLinkAlreadyDecidedError,
  ISmartLinkNoRunError,
  ISmartLinkRunInFlightError,
} from 'src/app/core/models/i-smart-link-error-responses';
import {
  ISmartLinkRunResponse,
  ISmartLinksResponse,
} from 'src/app/core/models/i-smart-links-response';
import { RestService } from './rest-service';

export interface ISmartLinkRunTriggerResult {
  runId: string;
  wasAlreadyRunning: boolean;
}

export type SmartLinksFetchResult =
  | { kind: 'no_run' }
  | { kind: 'running'; runId: string }
  | { kind: 'failed'; runId: string }
  | { kind: 'complete'; runId: string; smartLinks: ISmartLink[] };

export type SmartLinkDecisionResult =
  | { kind: 'decided'; smartLink: ISmartLink }
  | { kind: 'already_decided'; smartLink: ISmartLink };

export type SmartLinkRevokeResult =
  | { kind: 'revoked' }
  | { kind: 'not_revoked' };

@Injectable({
  providedIn: 'root',
})
export class SmartLinkingService {
  // The underscore is the api's own wire value for `domain` — it is also the `target_db` half
  // of `record_key` — so it is passed through verbatim rather than kebab-cased to match the
  // surrounding path segments.
  static readonly ENTITY_IDENTIFIER: string = 'smart-linking/private_markets';
  private readonly BASE_SMART_LINKING_ROUTE = `api/${SmartLinkingService.ENTITY_IDENTIFIER}`;
  private readonly BUILD_HTTP_ROUTE = (...args) =>
    this.restService.BUILD_HTTP_ROUTE(this.BASE_SMART_LINKING_ROUTE, ...args);

  constructor(private restService: RestService) {}

  triggerRun$(
    articleId: string,
    body: ISmartLinkRunRequest,
  ): Observable<ISmartLinkRunTriggerResult> {
    return this.restService
      .post$<ISmartLinkRunResponse>(
        this.BUILD_HTTP_ROUTE('articles', this.encodeArticleId(articleId), 'runs'),
        body,
      )
      .pipe(
        map((response) => ({
          runId: response.run_id,
          wasAlreadyRunning: false,
        })),
        catchError((error: HttpSimplifiedErrorResponse) => {
          const body = this.errorBody<ISmartLinkRunInFlightError>(error);
          if (error.status === 409 && body?.error === 'run_in_flight') {
            return of({ runId: body.run_id, wasAlreadyRunning: true });
          }
          return throwError(error);
        }),
      );
  }

  getSmartLinks$(articleId: string): Observable<SmartLinksFetchResult> {
    return this.restService
      .get$<ISmartLinksResponse>(
        this.BUILD_HTTP_ROUTE('articles', this.encodeArticleId(articleId), 'suggestions'),
      )
      .pipe(
        map((response): SmartLinksFetchResult => {
          if (response.status === 'complete') {
            return {
              kind: 'complete',
              runId: response.run_id,
              smartLinks: response.result?.smart_links ?? [],
            };
          }
          if (response.status === 'failed') {
            return { kind: 'failed', runId: response.run_id };
          }
          return { kind: 'running', runId: response.run_id };
        }),
        catchError((error: HttpSimplifiedErrorResponse) => {
          const body = this.errorBody<ISmartLinkNoRunError>(error);
          if (error.status === 404 && body?.error === 'no_run') {
            return of<SmartLinksFetchResult>({ kind: 'no_run' });
          }
          return throwError(error);
        }),
      );
  }

  decide$(
    articleId: string,
    smartLink: ISmartLink,
    action: SmartLinkDecisionAction,
    reason?: SmartLinkRejectReason,
  ): Observable<SmartLinkDecisionResult> {
    const request: ISmartLinkDecisionRequest =
      action === SmartLinkDecisionAction.REJECT
        ? { smart_link_id: smartLink.smart_link_id, action, reason }
        : { smart_link_id: smartLink.smart_link_id, action };

    return this.restService
      .post$<ISmartLink | null>(
        this.BUILD_HTTP_ROUTE('articles', this.encodeArticleId(articleId), 'decision'),
        request,
      )
      .pipe(
        map((card): SmartLinkDecisionResult => ({
          kind: 'decided',
          smartLink: card ?? this.applyDecision(smartLink, action, reason),
        })),
        catchError((error: HttpSimplifiedErrorResponse) => {
          const body = this.errorBody<ISmartLinkAlreadyDecidedError>(error);
          if (error.status === 409 && body?.error === 'already_decided') {
            // A suggestion holds one live decision at a time, so a 409 means this decision did
            // NOT take and the server is still holding the previous one. Merging the attempted
            // decision would assert the one outcome we know is false; returning the caller's
            // card unchanged lets the state service spot the mismatch and roll back.
            return of<SmartLinkDecisionResult>({
              kind: 'already_decided',
              smartLink: body.smart_link ?? smartLink,
            });
          }
          return throwError(error);
        }),
      );
  }

  revoke$(
    articleId: string,
    smartLink: ISmartLink,
  ): Observable<SmartLinkRevokeResult> {
    const request: ISmartLinkRevokeRequest = {
      smart_link_id: smartLink.smart_link_id,
      record_key: `${smartLink.target_db}#${smartLink.record_id}`,
    };

    return this.restService
      .post$<ISmartLinkRevokeResponse>(
        this.BUILD_HTTP_ROUTE('articles', this.encodeArticleId(articleId), 'revoke'),
        request,
      )
      .pipe(
        map((response): SmartLinkRevokeResult =>
          response?.outcome === 'revoked'
            ? { kind: 'revoked' }
            : { kind: 'not_revoked' },
        ),
      );
  }

  // Only for a 200 with no body, which is the proxy saying the run mapping expired after it had
  // already committed the decision — so merging locally is safe. Never reached for a 409, where
  // the decision did not commit.
  private applyDecision(
    smartLink: ISmartLink,
    action: SmartLinkDecisionAction,
    reason?: SmartLinkRejectReason,
  ): ISmartLink {
    return {
      ...smartLink,
      decision_action: action,
      decision_reason: reason ?? null,
    };
  }

  // errorMessage is typed string but carries the parsed JSON body when the server returns one.
  // Guarded so an HTML error page rethrows instead of half-matching a contract error.
  private errorBody<T extends { error: string }>(
    error: HttpSimplifiedErrorResponse,
  ): T | undefined {
    const body = error?.errorMessage as unknown;
    return typeof body === 'object' && body !== null ? (body as T) : undefined;
  }

  // BUILD_HTTP_ROUTE joins segments in raw, so the id has to arrive already encoded.
  private encodeArticleId(articleId: string): string {
    return encodeURIComponent(articleId);
  }
}
